import LocalData from '@issue-detail/components/IssueDialog/localData';
import { useRootInstance } from '@issue-detail/utils/useRootInstance';
import { useVuex } from '@issue-detail/utils/useVuex';
import { computed, nextTick, readonly, Ref, ref, shallowRef } from '@vue/composition-api';
import { createEventHook } from '@vueuse/core';

import { getIssueTypes } from '../Api/issue';
import { IssueType, IssueVO, DeleteIssueExtendVO } from '../Model/Issue';
import { RelationNavigationRecord } from './relationNavigation';
import { TurnPageOptionsPart } from './turnPage';

type IssueDrawerActionType = 'Create' | 'Edit' | 'PreviewType';

export interface IssueDrawerAction {
  type: IssueDrawerActionType;
}

export interface IssueDrawerActionPreview extends IssueDrawerAction {
  type: 'PreviewType';
  projectId: number;
  issueId?: number;
  issueTypeId: number;
  issueTypes: IssueType[];
}

export interface IssueDrawerActionCreate extends IssueDrawerAction {
  type: 'Create';
  projectId: number;
  issueId?: number;
  issueTypeId?: number;
  sprintId?: number | null;
  defaultData: Record<string, any>;
  copyFromIssueId?: number;
  keepCreateNext?: boolean;
}

export interface IssueDrawerActionEdit extends IssueDrawerAction {
  type: 'Edit';
  issueId: number;
  relationNavigationStack: RelationNavigationRecord[];
  force: boolean;
  extendsData?: { commentId?: number; subtaskId?: number },
  scrollTop?: [number, number];
}

export type AllIssueDrawerAction =
  | IssueDrawerActionPreview
  | IssueDrawerActionCreate
  | IssueDrawerActionEdit;

export const useIssueAction = (localDb: Ref<LocalData | undefined>) => {
  const action = shallowRef<AllIssueDrawerAction | null>(null);

  const instance = useRootInstance();
  const vuex = useVuex();
  const focusDrawer = ref(false);

  const closeEventHook = createEventHook<{
    isChanged: boolean;
    action: AllIssueDrawerAction | null;
    issueId?: number
  }>();

  const isChanged = ref(false);
  const isSubtaskChanged = ref(false);
  const turnPageOptions = ref<TurnPageOptionsPart>();

  const isContinueCreate = ref(false);

  const editIssue = (
    issueId: number,
    turnPage?: TurnPageOptionsPart,
    relationNavigationStack?: RelationNavigationRecord[],
    force: boolean = false,
    extendsData?: { commentId?: number; subtaskId?: number },
    scrollTop?: [number, number]
  ) => {
    isChanged.value = false;
    isSubtaskChanged.value = false;
    turnPageOptions.value = turnPage;
    isContinueCreate.value = false;
    isIssueDeleted.value = false;
    issueDeletedVO.value = null;
    if (action.value) {
      focusDrawer.value = true;
      action.value = null;
    }
    nextTick(() => {
      action.value = {
        type: 'Edit',
        issueId,
        relationNavigationStack: relationNavigationStack || [],
        force,
        extendsData,
        scrollTop
      };
      focusDrawer.value = false;
    });
  };

  const previewIssueType = async (projectId: number, issueTypeId: number) => {
    isChanged.value = false;
    markIssueIsDeleted(false);

    if (!projectId || !issueTypeId) {
      return;
    }

    focusDrawer.value = true;
    action.value = null;

    try {
      const issueTypes = await getIssueTypes(projectId);
      action.value = {
        type: 'PreviewType',
        projectId,
        issueTypeId,
        issueTypes
      };
    } finally {
      focusDrawer.value = false;
    }
  };

  const createIssue = async (
    projectId?: number,
    sprintId?: number,
    issueTypeId?: number,
    defaultData?: Record<string, any>,
    copyFromIssueId?: number,
    keepCreateNext = false
  ) => {
    isChanged.value = false;
    markIssueIsDeleted(false);

    projectId =
      projectId ||
      (vuex.state.app.currentProject as number) ||
      vuex.state.app.projectList.find(
        (p: any) => p.archived === 0 && p.recentVisit === 1
      )?.id;
    if (!projectId) {
      return;
    }
    focusDrawer.value = true;
    action.value = null;
    try {
      if (!focusDrawer.value) {
        return;
      }

      action.value = {
        type: 'Create',
        projectId,
        issueTypeId,
        sprintId,
        defaultData: defaultData || {},
        copyFromIssueId,
        keepCreateNext
      };

      isContinueCreate.value = keepCreateNext;
    } finally {
      focusDrawer.value = false;
    }
  };

  const close = (issueId?: number) => {
    focusDrawer.value = false;
    closeEventHook.trigger({
      isChanged: isChanged.value,
      action: action.value,
      issueId
    });
    action.value = null;
    turnPageOptions.value = undefined;
  };

  const markChanged = () => {
    isChanged.value = true;
  };

  const onDuplicateIssue = (item: IssueVO) => {
    close();
    instance.eventBus.$emit('issue:loadIssueList', item.id);
    instance.eventBus.$emit('issue:duplicate', item);
  };

  const onDeleteIssue = (item: IssueVO) => {
    close();
    instance.eventBus.$emit('issue:loadIssueList', item.id);
    instance.eventBus.$emit('issue:delete', item);
  };

  const isIssueDeleted = ref(false);
  const issueDeletedVO = ref<DeleteIssueExtendVO | null>(null)
  const markIssueIsDeleted = (isDelete = true, data: (DeleteIssueExtendVO | null) = null) => {
    isIssueDeleted.value = isDelete;
    focusDrawer.value = isDelete;
    action.value = null;
    issueDeletedVO.value = data;
  };

  return {
    action,
    actionType: computed(() => action.value?.type),
    editIssue,
    createIssue,
    previewIssueType,
    close,
    onClose: closeEventHook.on,
    onDuplicateIssue,
    onDeleteIssue,
    markChanged,
    isChanged,
    turnPageOptions,
    focusDrawer,
    localDb,
    isIssueDeleted: readonly(isIssueDeleted),
    issueDeletedVO,
    markIssueIsDeleted,
    isContinueCreate
  };
};

export type ActionStore = ReturnType<typeof useIssueAction>;
